import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { Supplier, SupplierFormData } from '../../types/supplier';
import MultiSelectDropdown from '../ui/MultiSelectDropdown';

interface SupplierFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (supplier: SupplierFormData) => Promise<void>;
  supplier?: Supplier | null;
  mode: 'create' | 'edit';
}

const SupplierFormModal: React.FC<SupplierFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  supplier,
  mode
}) => {
  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    gln: '',
    kreditorNumber: '',
    supplierCodeKuerzel: '',
    supplierCode: '',
    email: '',
    isActive: true,
    importSource: 'manual',
    assignedEK: []
  });

  // **EK-OPTIONEN DEFINIEREN**
  const ekOptions = [
    { value: 'EK0', label: 'EK0' },
    { value: 'EK1', label: 'EK1' },
    { value: 'EK2', label: 'EK2' },
    { value: 'EK3', label: 'EK3' },
    { value: 'EK4', label: 'EK4' },
    { value: 'EK5', label: 'EK5' },
    { value: 'EK6', label: 'EK6' },
    { value: 'EK7', label: 'EK7' },
    { value: 'EK8', label: 'EK8' },
    { value: 'EK9', label: 'EK9' },
    { value: 'EKV', label: 'EKV' },
    { value: 'EKD', label: 'EKD' },
    { value: 'EKW', label: 'EKW' },
    { value: 'EKG', label: 'EKG' },
    { value: 'EKSG', label: 'EKSG' }
  ];

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState(false);

  // **Form-Daten initialisieren**
  useEffect(() => {
    if (supplier && mode === 'edit') {
      setFormData({
        name: supplier.name,
        gln: supplier.gln || '',
        kreditorNumber: supplier.kreditorNumber || '',
        supplierCodeKuerzel: supplier.supplierCodeKuerzel || '',
        supplierCode: supplier.supplierCode || '',
        email: supplier.email,
        isActive: supplier.isActive,
        importSource: supplier.importSource || 'manual',
        assignedEK: supplier.assignedEK || []
      });
    } else {
      setFormData({
        name: '',
        gln: '',
        kreditorNumber: '',
        supplierCodeKuerzel: '',
        supplierCode: '',
        email: '',
        isActive: true,
        importSource: 'manual',
        assignedEK: []
      });
    }
    setErrors({});
  }, [supplier, mode, isOpen]);

  // **Automatische Supplier Code Generierung**
  const generateSupplierCode = (kreditorNr: string, kuerzel: string, name: string): string => {
    if (!kreditorNr || !name) return '';
    
    const formattedKuerzel = kuerzel ? (kuerzel.endsWith('.') ? kuerzel : `${kuerzel}.`) : '';
    return `${kreditorNr}-${formattedKuerzel}-${name}`;
  };

  // **Input-Handler mit automatischer Code-Generierung**
  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    // **Automatische Generierung des supplierCode wenn relevante Felder geändert werden**
    if (['kreditorNumber', 'supplierCodeKuerzel', 'name'].includes(field)) {
      const kreditorNr = field === 'kreditorNumber' ? value as string : formData.kreditorNumber || '';
      const kuerzel = field === 'supplierCodeKuerzel' ? value as string : formData.supplierCodeKuerzel || '';
      const name = field === 'name' ? value as string : formData.name || '';
      
      newFormData.supplierCode = generateSupplierCode(kreditorNr, kuerzel, name);
    }

    setFormData(newFormData);
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // **Validierung**
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Lieferantenname ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email-Adresse ist erforderlich';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Ungültiges Email-Format';
      }
    }

    if (formData.gln && formData.gln.length > 0) {
      if (formData.gln.length !== 14) {
        newErrors.gln = 'GLN muss 14 Zeichen lang sein';
      } else if (!/^\d+$/.test(formData.gln)) {
        newErrors.gln = 'GLN darf nur Zahlen enthalten';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // **Speichern**
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      
      // Spezifische Fehlermeldungen basierend auf dem Fehler
      let errorMessage = 'Fehler beim Speichern des Lieferanten';
      
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        if (serverMessage.includes('Namen') && serverMessage.includes('existiert bereits') || 
            serverMessage.includes('name') || 
            serverMessage.includes('E11000') && serverMessage.includes('name')) {
          errorMessage = 'Ein Lieferant mit diesem Namen existiert bereits. Bitte verwenden Sie einen anderen Namen.';
        } else if (serverMessage.includes('Email-Adresse bereits vorhanden') || 
            serverMessage.includes('email')) {
          errorMessage = 'Ein Lieferant mit dieser Email-Adresse existiert bereits. Bitte verwenden Sie eine andere Email-Adresse.';
        } else if (serverMessage.includes('GLN bereits vorhanden') || 
                   serverMessage.includes('gln')) {
          errorMessage = 'Ein Lieferant mit dieser GLN-Nummer existiert bereits.';
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // **Keyboard-Handler**
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.01 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.99 }}
          transition={{ duration: 0.02 }}
          className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-visible"
          onClick={(e) => e.stopPropagation()}
        >
          {/* **HEADER WIE VERSIONSHISTORY** */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-base font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faBuilding} className="text-red-500 mr-2 text-sm" />
                {mode === 'create' ? 'Neuer Lieferant' : 'Lieferant bearbeiten'}
              </h2>
              <p className="text-xs text-gray-600 mt-1">
                {mode === 'create' ? 'Neuen Lieferant anlegen' : `${supplier?.name} bearbeiten`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>

          {/* **CONTENT** */}
          <div className="p-4">
            {/* **Allgemeine Fehler** */}
            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{errors.general}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* **LIEFERANTENNAME** */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieferantenname <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="z.B. Mustermann GmbH"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              {/* **GLN-NUMMER** */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GLN-Nummer
                </label>
                <input
                  type="text"
                  value={formData.gln || ''}
                  onChange={(e) => handleInputChange('gln', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.gln ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="40123456789012"
                  maxLength={14}
                />
                {errors.gln && (
                  <p className="mt-1 text-xs text-red-600">{errors.gln}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">14-stellige Nummer (optional)</p>
              </div>

              {/* **KREDITOR-NUMMER** */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kreditor-Nummer
                </label>
                <input
                  type="text"
                  value={formData.kreditorNumber || ''}
                  onChange={(e) => handleInputChange('kreditorNumber', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.kreditorNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="803393"
                />
                {errors.kreditorNumber && (
                  <p className="mt-1 text-xs text-red-600">{errors.kreditorNumber}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Eindeutige Kreditor-Nummer für automatische Zuordnung beim Excel-Import</p>
              </div>

              {/* **LIEFERANTEN-CODE KÜRZEL** */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieferanten-Code Kürzel
                </label>
                <input
                  type="text"
                  value={formData.supplierCodeKuerzel || ''}
                  onChange={(e) => handleInputChange('supplierCodeKuerzel', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.supplierCodeKuerzel ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="DA. oder BLM."
                />
                {errors.supplierCodeKuerzel && (
                  <p className="mt-1 text-xs text-red-600">{errors.supplierCodeKuerzel}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Kurzes Kürzel für den Lieferanten (z.B. "DA.", "BLM.") - wird automatisch mit "." ergänzt</p>
              </div>

              {/* **GENERIERTER SUPPLIER CODE (READ-ONLY)** */}
              {formData.supplierCode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Generierter Supplier Code (Perfion-Format)
                  </label>
                  <input
                    type="text"
                    value={formData.supplierCode}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-600"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Wird automatisch generiert aus: KreditorNr-Kürzel-Name
                  </p>
                </div>
              )}

              {/* **EMAIL-ADRESSE** */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email-Adresse <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="kontakt@lieferant.de"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>

              {/* **EK-BEREICHE ZUORDNUNG** */}
              <div>
                <MultiSelectDropdown
                  label="EK-Bereiche"
                  options={ekOptions}
                  selectedValues={formData.assignedEK || []}
                  onChange={(values) => handleInputChange('assignedEK', values)}
                  placeholder="EK-Bereiche auswählen..."
                  error={errors.assignedEK}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Zuordnung zu EK-Bereichen für Berechtigungsfilterung
                </p>
              </div>

              {/* **STATUS** */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                >
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                </select>
              </div>
            </div>
          </div>

          {/* **FOOTER WIE VERSIONSHISTORY** */}
          <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors text-sm"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                  Speichern...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  {mode === 'create' ? 'Erstellen' : 'Speichern'}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SupplierFormModal;